import React, { useState } from 'react';
import { HelpCircle, BookOpen, ChevronDown, ChevronUp, FileText, Target, Brain, Sparkles, MapPin, MousePointer2, Settings } from 'lucide-react';

export const Help = ({ setActiveTab }) => {
    const [openFaq, setOpenFaq] = useState(null);

    const faqs = [
        {
            question: "How do I update or change my CV?",
            answer: "Navigate to the 'AI Resume Builder' via the Sidebar or click your profile picture and select 'AI Resume Builder'. In the builder, you can click the 'Upload Resume' tab to upload a new PDF/DOCX file, or use the 'Manual Editor' tab to edit details directly. Once updated, all your career recommendations and matching scores will automatically refresh.",
            icon: <FileText className="text-blue-500" />
        },
        {
            question: "How does Job Matching work?",
            answer: "The 'Job Matching' engine analyzes your resume data against real-time job listings. It calculates a compatibility score based on skills, experience, and location. For the best results, ensure your resume is fully updated in the 'AI Resume Builder'.",
            icon: <Target className="text-purple-500" />
        },
        {
            question: "Map Analysis vs Career Copilot?",
            answer: "Map Analysis is for 'Basic' exploration, showing immediate career paths and skill gaps. Career Copilot is our 'Advanced' AI assistant that provides personalized career roadmaps, industry trends, and strategic advice tailored to your long-term goals.",
            icon: <MapPin className="text-orange-500" />
        },
        {
            question: "How do I prepare for an interview?",
            answer: "Use our 'Interview' suite! You can generate specific AI questions in 'Interview AI Questions', or engage in a real-time 'Mock Interview' simulation. For live assistance during a call, use 'Interview Copilot'.",
            icon: <Sparkles className="text-yellow-500" />
        }
    ];

    const guideSteps = [
        {
            title: "1. Build Your Base",
            description: "Complete your profile in the AI Resume Builder. This is the source of truth for the AI.",
            icon: <FileText size={20} />
        },
        {
            title: "2. Explore Paths",
            description: "Use Map Analysis to see where you stand, or Chat with the Career Copilot.",
            icon: <Brain size={20} />
        },
        {
            title: "3. Target Jobs",
            description: "Run Job Matching to find best-fit roles and generate tailored Cover Letters.",
            icon: <Target size={20} />
        },
        {
            title: "4. Ace Interviews",
            description: "Practice with Mock Interviews and use the Interview Copilot during calls.",
            icon: <MousePointer2 size={20} />
        }
    ];

    return (
        <div className="py-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <header className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
                <div>
                    <div className="flex items-center gap-3 mb-3">
                        <div className="p-2.5 bg-[#3c0f6c]/10 rounded-xl">
                            <HelpCircle size={28} className="text-[#3c0f6c]" />
                        </div>
                        <h1 className="text-3xl font-extrabold text-[#0E0E0E] tracking-tight">Help Center</h1>
                    </div>
                    <p className="text-gray-500 text-lg max-w-xl">Master the JobStep and navigate your career journey with efficiency.</p>
                </div>
                <button
                    onClick={() => setActiveTab('dashboard')}
                    className="px-6 py-2.5 bg-white border border-gray-200 rounded-xl font-semibold text-sm hover:bg-gray-50 transition-all flex items-center gap-2 shadow-sm"
                >
                    Back to Dashboard
                </button>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                <div className="lg:col-span-2 space-y-12">
                    <section>
                        <div className="flex items-center gap-3 mb-6">
                            <BookOpen size={22} className="text-[#3c0f6c] opacity-70" />
                            <h2 className="text-2xl font-bold text-[#0E0E0E]">Quick Start Guide</h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            {guideSteps.map((step, idx) => (
                                <div key={idx} className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm hover:shadow-md transition-all group">
                                    <div className="w-10 h-10 bg-[#3c0f6c]/5 rounded-xl flex items-center justify-center text-[#3c0f6c] mb-4 group-hover:bg-[#3c0f6c] group-hover:text-white transition-colors">
                                        {step.icon}
                                    </div>
                                    <h3 className="font-bold text-lg mb-2 text-[#0E0E0E]">{step.title}</h3>
                                    <p className="text-gray-500 text-sm leading-relaxed">{step.description}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-[#0E0E0E] mb-6">Frequently Asked Questions</h2>
                        <div className="space-y-4">
                            {faqs.map((faq, idx) => (
                                <div key={idx} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden transition-all">
                                    <button
                                        onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                                        className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50/50 transition-colors"
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className="shrink-0">{faq.icon}</div>
                                            <span className="font-semibold text-gray-800">{faq.question}</span>
                                        </div>
                                        {openFaq === idx ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
                                    </button>
                                    {openFaq === idx && (
                                        <div className="px-5 pb-5 pt-0 text-gray-600 leading-relaxed animate-in fade-in slide-in-from-top-1 duration-300">
                                            <div className="pt-4 border-t border-gray-50">
                                                {faq.answer}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </section>
                </div>

                <div className="space-y-6">
                    <div className="bg-[#3c0f6c] text-white p-8 rounded-[32px] shadow-xl relative overflow-hidden group">
                        <div className="absolute -top-6 -right-6 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                            <Settings size={180} />
                        </div>
                        <h3 className="text-2xl font-bold mb-4">Pro Tip: Your Context</h3>
                        <p className="text-purple-100/90 text-sm leading-relaxed mb-6">
                            The AI models work best when they have a complete picture. Keep your <strong>Experience</strong> and <strong>Skills</strong> detailed in the builder.
                        </p>
                        <div className="p-4 bg-white/10 rounded-2xl border border-white/20 backdrop-blur-sm">
                            <div className="flex items-center gap-3 mb-2">
                                <FileText size={16} className="text-[#FFD600]" />
                                <span className="font-bold text-xs uppercase tracking-wider text-[#FFD600]">Live Context</span>
                            </div>
                            <p className="text-[11px] text-purple-50 leading-normal">Updates in your resume immediately trigger refreshes for Scores and Matches app-wide.</p>
                        </div>
                    </div>

                    <div className="bg-[#FFD600] p-8 rounded-[32px] shadow-sm flex flex-col items-center text-center">
                        <h3 className="text-xl font-extrabold text-[#0E0E0E] mb-3">Questions?</h3>
                        <p className="text-[#0E0E0E]/70 mb-6 text-sm font-medium">
                            Having technical issues or need specific career guidance?
                        </p>
                        <button className="w-full bg-[#0E0E0E] text-white py-3.5 rounded-xl font-bold hover:scale-[1.02] transition-all shadow-lg active:scale-100">
                            Contact Support
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
